import React, { useState, useRef, useEffect } from "react";
import "amazon-connect-chatjs";
import { useMutation } from "@apollo/react-hooks";
import { POST_INITIATE_CHAT } from "../../queries";
import { ButtonLoader, BottomDrawer, ChatBubble } from "../../components";
import { Form, Field } from "react-final-form";
import createDecorator from "final-form-focus";
import {
  Grid,
  TextField,
  Typography,
  Box,
  makeStyles,
  Button,
  Paper,
  Divider
} from "@material-ui/core";
import { Loader } from "./Loader";
import PropTypes from "prop-types";
import { IntroText } from "./Introtext";
import { useHistory } from "react-router-dom";
import { CHAT_EVENTS } from "../../constants";

const focusOnErrors = createDecorator();

const useClasses = makeStyles(theme => ({
  meta: {
    fontSize: 12,
    paddingTop: "3px",
    color: theme.palette.grey[500]
  },
  chatBubbleCustomer: {
    alignSelf: "flex-end"
  },
  chatBubbleOperator: {
    alignSelf: "flex-start"
  }
}));

const CONNECTION_ERROR_MESSAGE =
  "We are experiencing higher than normal phone, chat, and email support requests due to the incredible increase in COVID-19 testing because of the rise in cases. Our agents are working diligently to help as many people as possible, as fast as possible. Please continue selecting retry to reach an agent. You can also reach us at a 1-877-837-8461 or support@gogettested.com. We appreciate your patience with us.";

const ChatWidget = ({
  participantDetails,
  instanceId,
  contactFlowId,
  emailTranscript = false
}) => {
  const history = useHistory();
  const classes = useClasses();

  const chatBottom = useRef(null);

  const [connectionEstablished, setConnectionEstablished] = useState(false);
  const [messages, setMessages] = useState([]);
  const [chatSession, setChatSession] = useState();
  const [sendingMessage, setSendingMessage] = useState(false);
  const [drawerClearance, setDrawerClearance] = useState(0);
  const [chatEnded, setChatEnded] = useState(false);
  const [loadingTranscript, setLoadingTranscript] = useState(false);
  const [error, setError] = useState("");
  const [agentIsTyping, setAgentIsTyping] = useState(false);

  const initiateChat = () => {
    postInitiateChat({
      variables: {
        body: {
          ParticipantDetails: participantDetails,
          ContactFlowId: contactFlowId,
          InstanceId: instanceId
        }
      }
    });
  };

  /**
   * Initiate chat on mount
   */
  useEffect(() => {
    initiateChat();
  }, []);

  /**
   * Scroll to last message on messages update
   */
  useEffect(() => {
    if (!chatBottom || !chatBottom.current) return;

    chatBottom.current.scrollIntoView({
      behavior: "smooth",
      block: "end"
    });
  }, [messages, chatBottom, agentIsTyping]);

  /**
   * Get participant display name
   *
   * @param {string} name
   */
  const getParticipantDisplayName = name =>
    name === "BOT" || name === "SYSTEM_MESSAGE" ? "GGT" : name;

  /**
   * Handle chat typing
   *
   * @param {*} data
   */
  const handleTyping = ({ data }) => {
    if (data.ParticipantRole && data.ParticipantRole !== "CUSTOMER") {
      setAgentIsTyping(true);
    }
  };

  /**
   * Handle messages
   *
   * @param {*} data
   */
  const handleMessage = data => {
    setMessages(prevState => [...prevState, data]);

    if (data.ParticipantRole !== "CUSTOMER") {
      setAgentIsTyping(false);
    }
  };

  /**
   * Handle connection established
   *
   * @param {*} data
   */
  const handleConnectionEstablished = () => {
    setError("");
    setConnectionEstablished(true);
  };

  /**
   * Handle chat ended
   *
   * @param {*} data
   */
  const handleEnded = () => {
    chatSession.sendEvent({ contentType: CHAT_EVENTS.PARTICIPANT_LEFT });
    chatSession.disconnectParticipant();

    setChatEnded(true);
    setChatSession();
    setConnectionEstablished(false);
  };

  /**
   * Handle connection broken
   *
   * @param {*} data
   */
  const handleConnectionBroken = () => setError("Connection broken");

  /**
   * Initiate chat with API
   */
  const [postInitiateChat, { loading }] = useMutation(POST_INITIATE_CHAT, {
    onCompleted: ({ contact = {} }) => {
      const { data = {} } = contact;

      if (connect && data && data.startChatResult) {
        const session = connect.ChatSession.create({
          chatDetails: data.startChatResult,
          type: connect.ChatSession.SessionTypes.CUSTOMER,
          options: {
            region: "us-east-1"
          }
        });

        if (session) {
          session.onTyping(handleTyping);
          session.onMessage(handleMessage);
          session.onConnectionEstablished(handleConnectionEstablished);
          session.onEnded(handleEnded);
          session.onConnectionBroken(handleConnectionBroken);

          session
            .connect()
            .then(() => setChatSession(session))
            .catch(() => setError(CONNECTION_ERROR_MESSAGE));
        }
      }
    },
    onError: () => {
      setError(CONNECTION_ERROR_MESSAGE);
    }
  });

  const handleMessageChange = (e, input) => {
    // Trigger final form onChange
    input.onChange(e.target.value);

    // Send typing event
    chatSession.sendEvent({
      contentType: CHAT_EVENTS.TYPING
    });
  };

  /**
   * Handle submit message
   *
   * @param {*} data
   * @param {*} form
   */
  const handleSubmitMessage = async (data, form) => {
    setSendingMessage(true);

    chatSession
      .sendMessage({
        message: data.message,
        contentType: "text/plain"
      })
      .then(() => {
        form.reset();
        setSendingMessage(false);
      });
  };

  /**
   * Handle Get transcript
   */
  const handleGetTranscript = () => {
    setLoadingTranscript(true);

    chatSession
      .getTranscript({
        scanDirection: "BACKWARD",
        sortOrder: "ASCENDING",
        maxResults: 15
      })
      .then(({ data }) => {
        setLoadingTranscript(false);
      });
  };

  return (
    <>
      {error ? (
        <>
          <Typography color="error">{error}</Typography>
          <Box my={2}>
            <ButtonLoader
              variant="contained"
              loading={loading}
              onClick={() => initiateChat()}
            >
              Retry
            </ButtonLoader>
          </Box>
        </>
      ) : (
        <>
          {!chatEnded && (!connectionEstablished || loading) ? (
            <Loader />
          ) : (
            <Box>
              {!chatEnded ? (
                <>
                  <Typography paragraph className={classes.meta}>
                    Chat is connected
                  </Typography>
                  {messages.length > 0 && (
                    <Grid container direction="column">
                      {messages
                        .filter(message => message.data.Type === "MESSAGE")
                        .map(({ data }, index) => (
                          <Grid
                            item
                            xs={10}
                            className={
                              data.ParticipantRole === "CUSTOMER"
                                ? classes.chatBubbleCustomer
                                : classes.chatBubbleOperator
                            }
                            key={`message-${index}`}
                          >
                            <ChatBubble
                              message={data.Content}
                              name={getParticipantDisplayName(data.DisplayName)}
                              time={data.AbsoluteTime}
                              align={
                                data.ParticipantRole === "CUSTOMER"
                                  ? "right"
                                  : "left"
                              }
                              backgroundColor={
                                data.ParticipantRole === "CUSTOMER"
                                  ? "#fff"
                                  : "#f1f1f1"
                              }
                            />
                          </Grid>
                        ))}
                    </Grid>
                  )}
                  {agentIsTyping && (
                    <Typography paragraph className={classes.meta}>
                      Agent is typing...
                    </Typography>
                  )}
                  <Box
                    ref={chatBottom}
                    style={{
                      height: `${drawerClearance}px`
                    }}
                  />
                  <BottomDrawer setClearance={setDrawerClearance}>
                    <Form
                      onSubmit={handleSubmitMessage}
                      initialValues={{}}
                      decorators={[focusOnErrors]}
                      render={({ handleSubmit, submitErrors: errors = {} }) => (
                        <form
                          data-cy="chat-send-message"
                          onSubmit={handleSubmit}
                        >
                          <Field
                            name="message"
                            render={({ input }) => (
                              <TextField
                                {...input}
                                onChange={e => handleMessageChange(e, input)}
                                fullWidth
                                margin="normal"
                                variant="outlined"
                                error={!!errors.message}
                                helperText={errors.message}
                                inputRef={input => input && input.focus()}
                                InputProps={{
                                  inputProps: {
                                    "data-lpignore": "true",
                                    "data-cy": "input-message"
                                  }
                                }}
                              />
                            )}
                          />
                          <Grid container spacing={2}>
                            <Grid item>
                              <Button
                                size="large"
                                onClick={() => handleEnded()}
                              >
                                End Chat
                              </Button>
                            </Grid>
                            <Grid item xs>
                              <ButtonLoader
                                type="submit"
                                fullWidth
                                variant="contained"
                                color="primary"
                                loading={sendingMessage}
                                size="large"
                              >
                                Send Message
                              </ButtonLoader>
                            </Grid>
                          </Grid>
                        </form>
                      )}
                    />
                  </BottomDrawer>
                </>
              ) : (
                <Paper>
                  <Box p={2}>
                    <IntroText>Thank you for chatting with us today.</IntroText>
                    {emailTranscript ? (
                      <>
                        <ButtonLoader
                          onClick={() => handleGetTranscript()}
                          color="primary"
                          variant="contained"
                          fullWidth
                          loading={loadingTranscript}
                          size="large"
                        >
                          Email Transcript
                        </ButtonLoader>
                        <Box my={2}>
                          <Divider />
                        </Box>
                      </>
                    ) : (
                      <Button
                        onClick={() => history.goBack()}
                        size="large"
                        variant="contained"
                        color="default"
                        fullWidth
                      >
                        Go Back
                      </Button>
                    )}
                  </Box>
                </Paper>
              )}
            </Box>
          )}
        </>
      )}
    </>
  );
};

ChatWidget.propTypes = {
  participantDetails: PropTypes.shape({
    DisplayName: PropTypes.string
  }).isRequired,
  instanceId: PropTypes.string.isRequired,
  contactFlowId: PropTypes.string.isRequired,
  emailTranscript: PropTypes.bool
};

export { ChatWidget };
